import React, { useEffect, useRef, useState } from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography } from "@mui/material";
import { useDispatch } from "react-redux";
import BoardGateWarningDialog from "./BoardGateWarningDialog";
import { completeGroup } from "../redux/ducks/group";
import { completeProject } from "../redux/slices/project";
import { showAlert } from "../redux/slices/alerts";
import { triggerRefresh } from "../redux/slices/refreshTriggers";

const COPY = {
    group: {
        title: "Complete this group?",
        body: (name) => `"${name}" and every project and task inside it will be marked complete.`,
        action: "Complete group",
    },
    project: {
        title: "Complete this project?",
        body: (name) => `"${name}" and every open task inside it will be marked complete.`,
        action: "Complete project",
    },
};

const summarise = (response) => {
    const parts = [];
    if (typeof response.completedProjects === "number") {
        parts.push(`${response.completedProjects} project${response.completedProjects === 1 ? "" : "s"}`);
    }
    const tasks = response.completedTasks || 0;
    parts.push(`${tasks} task${tasks === 1 ? "" : "s"}`);
    return `${parts.join(" and ")} completed`;
};

const describeLeftOpen = (skipped, hidden) => {
    const parts = [];
    if (skipped.length > 0) {
        parts.push(`${skipped.length} item${skipped.length === 1 ? " was" : "s were"} left open — the reason is listed against each one below.`);
    }
    if (hidden > 0) {
        parts.push(`${hidden} task${hidden === 1 ? " you cannot see was" : "s you cannot see were"} left open.`);
    }
    return parts.join(" ");
};

const CompleteCascadeDialog = ({ target, onClose, onCompleted, confirm = true, onRunningChange }) => {
    const dispatch = useDispatch();
    const [running, setRunning] = useState(false);
    const [gate, setGate] = useState(null);
    const autoRunFor = useRef(null);

    const copy = COPY[target?.type] || COPY.group;

    useEffect(() => {
        if (!onRunningChange) return undefined;
        onRunningChange(running);
        return () => onRunningChange(false);
    }, [running, onRunningChange]);

    const close = () => {
        setGate(null);
        onClose();
    };

    const run = (bypass) => {
        setRunning(true);
        const done = (response) => {
            setRunning(false);
            const ok = target.type === "group" ? response?.group : response?.project;
            if (!ok) {
                dispatch(showAlert({ severity: "error", message: `Failed to complete ${target.type}` }));
                if (!confirm) close();
                return;
            }

            const skipped = response.skipped || [];
            const hidden = response.hiddenOpenCount || 0;
            const summary = summarise(response);
            dispatch(triggerRefresh({ triggerName: "groups" }));
            if (onCompleted) onCompleted();

            if (skipped.length > 0 || hidden > 0) {
                const retryable = skipped.some((item) => item?.bypassable);
                setGate({
                    title: "Not everything was completed",
                    message: `${summary}. ${describeLeftOpen(skipped, hidden)}`,
                    blockers: skipped,
                    retryable,
                });
                return;
            }

            setGate(null);
            onClose();
            dispatch(showAlert({ severity: "success", message: `${target.name} completed — ${summary}.` }));
        };

        if (target.type === "group") {
            dispatch(completeGroup(target.id, { bypass }, done));
        } else {
            dispatch(completeProject({ projectId: target.id, options: { bypass }, callback: done }));
        }
    };

    useEffect(() => {
        if (confirm || !target?.id) {
            autoRunFor.current = null;
            return;
        }
        if (autoRunFor.current === target.id) return;
        autoRunFor.current = target.id;
        run(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [confirm, target?.id]);

    return (
        <>
            <Dialog open={confirm && Boolean(target) && !gate} onClose={close} maxWidth="xs" fullWidth>
                <DialogTitle>{copy.title}</DialogTitle>
                <DialogContent>
                    <Typography>
                        {copy.body(target?.name)} Anything that is blocked or has an unfinished QA board is left open and listed afterwards.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={close} disabled={running}>Cancel</Button>
                    <Button onClick={() => run(false)} variant="contained" disabled={running}>
                        {running ? "Completing…" : copy.action}
                    </Button>
                </DialogActions>
            </Dialog>

            <BoardGateWarningDialog
                open={Boolean(gate)}
                gate={gate}
                onClose={close}
                onBypass={gate?.retryable ? () => run(true) : undefined}
                bypassLabel="Complete these anyway"
            />
        </>
    );
};

export default CompleteCascadeDialog;
